/**
 * @fileoverview Indicador visual de salvamento (saving/saved/error).
 *
 * Sistema de eventos global para comunicar estado de salvamento:
 * - emitSaveState(): emite estado para todos os listeners
 * - useSaveIndicator(): hook que escuta mudanças de estado
 * - withSaveIndicator(): wrapper para funções async de save
 *
 * O componente SaveIndicator exibe pill animada no canto superior direito.
 * Auto-reset para idle após 2.5s do estado "saved".
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { Cloud, CloudOff, Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Global save events bus */
const listeners = new Set<(state: SaveState) => void>();

export function emitSaveState(state: SaveState) {
  listeners.forEach(fn => fn(state));
}

/** Auto-reset after showing "saved" for 2s */
export function useSaveIndicator() {
  const [state, setState] = useState<SaveState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handler = (s: SaveState) => {
      setState(s);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (s === 'saved') {
        timerRef.current = setTimeout(() => setState('idle'), 2500);
      }
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return state;
}

/** Wrap any async save fn to automatically emit save state */
export function withSaveIndicator<T extends (...args: any[]) => Promise<any>>(fn: T): T {
  return (async (...args: any[]) => {
    emitSaveState('saving');
    try {
      const result = await fn(...args);
      emitSaveState('saved');
      return result;
    } catch (e) {
      emitSaveState('error');
      throw e;
    }
  }) as T;
}

/** Visual save indicator component — place in the app layout header */
export default function SaveIndicator() {
  const state = useSaveIndicator();

  if (state === 'idle') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-300 animate-in fade-in-0',
        state === 'saving' && 'bg-warning/10 text-warning border border-warning/20',
        state === 'saved' && 'bg-success/10 text-success border border-success/20',
        state === 'error' && 'bg-destructive/10 text-destructive border border-destructive/20',
      )}
    >
      {state === 'saving' && <><Loader2 className="h-3 w-3 animate-spin" /> Salvando…</>}
      {state === 'saved' && <><Check className="h-3 w-3" /> Salvo</>}
      {state === 'error' && <><CloudOff className="h-3 w-3" /> Erro ao salvar</>}
    </div>
  );
}
